import React, { useEffect, useRef } from 'react';
import { SxProps } from '@mui/system';
import { hexToRgba, StacklineTextVariant } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { ProductImage } from 'src/components/BeaconV2Components/ProductCard';
import { BeaconProImageFallback, SnackbarCancel, SnackbarImportant } from 'src/components/SvgIcons';
import { CheckmarkSolidCircle } from 'src/components/SvgIcons/CheckmarkSolidCircle';
import Hls from 'hls.js';
import colors from 'src/utils/colors';

export const ContentCardHeader = ({ title, children }: { children?: React.ReactNode; title: string }) => {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Text variant="h4" sx={{ fontSize: '16px', fontWeight: '700', textTransform: 'uppercase' }}>
        {title}
      </Text>
      {children}
    </Flex>
  );
};

export const ContentUtilizationStatus = ({
  currentUtilization,
  maxUtilization,
  score,
  label
}: {
  currentUtilization: number;
  maxUtilization: number;
  score: number;
  label: 'character' | 'image' | 'video';
}) => {
  const pluralizedLabel = label + (maxUtilization > 1 ? 's' : '');
  return (
    <Flex flexDirection="row" alignItems="center" gap={8}>
      <Text variant="body2" fontWeight="500">{`${currentUtilization}/${maxUtilization} ${pluralizedLabel}`}</Text>
      <UtilizationBadge value={score * 100} />
    </Flex>
  );
};

export const ContentCardContainer = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  return (
    <Flex borderRadius="8px" padding="32px 0" flexDirection="column" gap={16} sx={sx}>
      {children}
    </Flex>
  );
};

const COLOR_MAP = {
  high: '#185450',
  medium: '#E6A00C',
  low: '#FF534A'
};

const BACKGROUND_COLOR_MAP = {
  high: '#E3F3E6',
  medium: '#FFF2D6',
  low: '#FFEEED'
};
const UtilizationBadge = ({ value }: { value: number }) => {
  const status = value >= 70 ? 'high' : value >= 50 ? 'medium' : 'low';
  return (
    <Flex
      sx={{
        borderRadius: '24px',
        backgroundColor: BACKGROUND_COLOR_MAP[status],
        padding: '4px',
        paddingRight: '8px',
        minHeight: '32px'
      }}
      alignItems="center"
    >
      <Flex
        sx={{
          padding: '0 8px',
          borderRadius: '24px',
          backgroundColor: COLOR_MAP[status],
          minHeight: '24px'
        }}
        alignItems="center"
      >
        <Text color="white" variant="body3" fontWeight="700">
          {status}
        </Text>
      </Flex>
      <Text sx={{ marginLeft: '8px', color: COLOR_MAP[status] }} variant="body2" fontWeight="500">
        {value.toFixed(1)}%
      </Text>
    </Flex>
  );
};

export const AccuracyBadge = ({ isAccurate = false }: { isAccurate?: boolean | null }) => {
  const { color, backgroundColor, icon, text } = (() => {
    switch (isAccurate) {
      case true:
        return {
          color: colors.darkGreen,
          backgroundColor: colors.pastelGreen,
          icon: <CheckmarkSolidCircle color={colors.darkGreen} style={{ width: '24px' }} />,
          text: 'Accurate'
        };
      case false:
        return {
          color: colors.errorRed,
          backgroundColor: hexToRgba(colors.errorRed, 0.1),
          icon: <SnackbarCancel style={{ width: '24px' }} />,
          text: 'Inaccurate'
        };
      case null:
        return {
          color: colors.gold,
          backgroundColor: '#FFF8EA',
          icon: <SnackbarImportant style={{ width: '24px' }} />,
          text: 'Master content missing'
        };
      default:
        return {
          color: null,
          backgroundColor: null,
          icon: null,
          text: null
        };
    }
  })();

  return (
    <Flex
      sx={{
        borderRadius: '24px',
        backgroundColor,
        padding: '4px',
        paddingRight: '8px',
        minHeight: '32px'
      }}
      alignItems="center"
    >
      {icon}
      <Text sx={{ marginLeft: '8px', color }} variant="body2" fontWeight="500">
        {text}
      </Text>
    </Flex>
  );
};

export const ContentTextContainer = (props: {
  children: React.ReactNode;
  textVariant?: StacklineTextVariant;
  textSx?: SxProps;
}) => {
  return (
    <div
      style={{
        height: '100%'
      }}
      className="ContentTextContainer"
    >
      <Text variant={props.textVariant || 'body2'} sx={props.textSx}>
        {props.children ?? 'Missing required content.'}
      </Text>
    </div>
  );
};

export const ContentAccuracyImageCard = ({
  imageUri,
  mainDisplayTitle,
  secondaryDisplayTitle,
  metrics
}: ImageCardProps) => {
  const { resolution, ratio } = metrics;
  return (
    <ContentAccuracyImageCardContainer
      image={
        <ProductImage
          width="152px"
          height="152px"
          fallbackImageStyle={{ width: '172px', height: '172px', margin: '0' }}
          imageUri={imageUri}
          mainDisplayTitle={mainDisplayTitle}
          secondaryDisplayTitle={secondaryDisplayTitle}
          imageStyle={{ objectFit: 'fill' }}
        />
      }
      metrics={
        <>
          <Text variant="h6">Metrics</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            <MetricText label="Resolution" value={resolution} />
            <MetricText label="Ratio" value={ratio} />
          </Flex>
        </>
      }
    />
  );
};

export const ContentAccuracyImageCardContainer = ({
  image,
  metrics
}: {
  image: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <Flex sx={{ borderRadius: '8px', border: '1px solid #E8E8ED', minWidth: '445px', minHeight: '200px' }}>
      <Flex sx={{ padding: '24px' }} justifyContent="center" alignItems="center" minWidth="200px">
        {image}
      </Flex>
      <Flex
        sx={{
          backgroundColor: '#FBFBFB',
          padding: '24px 20px',
          borderLeft: '1px solid #E8E8ED',
          minWidth: '244px',
          borderBottomRightRadius: '8px',
          borderTopRightRadius: '8px',
          borderRight: '1px solid #E8E8ED'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </Flex>
  );
};

export const ContentAccuracyVideoCardContainer = ({
  video,
  metrics
}: {
  video: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <div style={{ borderRadius: '8px', border: '1px solid #E8E8ED', maxWidth: '443px', width: '443px' }}>
      <Flex sx={{ padding: '24px', minHeight: '266px' }} justifyContent="center" alignItems="center">
        {video}
      </Flex>
      <Flex
        sx={{
          backgroundColor: '#FBFBFB',
          padding: '16px 24px',
          borderTop: '1px solid #E8E8ED',
          height: '83px',
          borderBottomRightRadius: '8px',
          borderBottomLeftRadius: '8px'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </div>
  );
};

export const ContentAccuracyVideoCard = ({ videoSrc, metrics }: VideoCardProps) => {
  const { resolution, length } = metrics;

  if (videoSrc === null) {
    return (
      <ContentAccuracyVideoCardContainer
        video={<BeaconProImageFallback style={{ width: '160px', height: '160px', margin: '0' }} />}
        metrics={
          <>
            <Text variant="h6">Missing required video</Text>
            <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
              Metric details will be displayed here once a video is uploaded.
            </Flex>
          </>
        }
      />
    );
  }

  return (
    <ContentAccuracyVideoCardContainer
      video={<HlsVideoPlayer videoSrc={videoSrc} width="395px" height="218px" videoStyle={{ minWidth: '395px' }} />}
      metrics={
        <>
          <Text variant="h6">Metrics</Text>
          <Flex sx={{ marginTop: '8px' }} gap={16}>
            <MetricText label="Resolution" value={resolution} />
            <MetricText label="Length" value={length} />
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationImageCardContainer = ({
  image,
  metrics
}: {
  image: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <Flex sx={{ borderRadius: '8px', border: '1px solid #E8E8ED', width: '475px', minHeight: '224px' }}>
      <Flex sx={{ padding: '24px', width: '222px' }} justifyContent="center" alignItems="center">
        {image}
      </Flex>
      <Flex
        sx={{
          borderTopRightRadius: '8px',
          borderBottomRightRadius: '8px',
          backgroundColor: '#FBFBFB',
          borderLeft: '1px solid #E8E8ED',
          width: '252px',
          padding: '24px'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </Flex>
  );
};

interface ImageCardProps {
  imageUri: string;
  mainDisplayTitle: string;
  secondaryDisplayTitle: string;
  metrics: {
    resolution: string;
    ratio: string;
  };
}

export const ContentUtilizationImageCard = ({
  imageUri,
  mainDisplayTitle,
  secondaryDisplayTitle,
  metrics
}: ImageCardProps) => {
  const { resolution, ratio } = metrics;
  return (
    <ContentUtilizationImageCardContainer
      image={
        <ProductImage
          width="176px"
          height="176px"
          fallbackImageStyle={{ width: '152px', height: '152px', margin: '0' }}
          imageUri={imageUri}
          mainDisplayTitle={mainDisplayTitle}
          secondaryDisplayTitle={secondaryDisplayTitle}
        />
      }
      metrics={
        <>
          <Text variant="h6">Metrics</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            <MetricText label="Resolution" value={resolution} />
            <MetricText label="Ratio" value={ratio} />
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationImageCardPlaceholder = () => {
  return (
    <ContentUtilizationImageCardContainer
      image={<BeaconProImageFallback style={{ width: '148px', height: '148px', margin: '0' }} />}
      metrics={
        <>
          <Text variant="h6">Missing required image</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            Metric details will be displayed here once an image is uploaded.
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationVideoCardPlaceholder = () => {
  return (
    <ContentUtilizationVideoCardContainer
      video={<BeaconProImageFallback style={{ width: '160px', height: '160px', margin: '0' }} />}
      metrics={
        <>
          <Text variant="h6">Missing required video</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            Metric details will be displayed here once a video is uploaded.
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationVideoCardContainer = ({
  video,
  metrics
}: {
  video: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <div
      style={{
        borderRadius: '8px',
        border: '1px solid #E8E8ED',
        borderBottom: '0px',
        width: '475px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Flex sx={{ height: '238px', width: '428px', marginTop: '24px' }} justifyContent="center" alignItems="center">
        {video}
      </Flex>
      <Flex
        sx={{
          backgroundColor: '#FBFBFB',
          padding: '15px 24px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          border: '1px solid #E8E8ED',
          marginTop: '24px'
        }}
        flexDirection="column"
        justifyContent="center"
        width="475px"
      >
        {metrics}
      </Flex>
    </div>
  );
};

interface VideoCardProps {
  videoSrc: string;
  metrics: {
    resolution: string;
    length: string;
  };
}

export const ContentUtilizationVideoCard = ({ videoSrc, metrics }: VideoCardProps) => {
  const { resolution, length } = metrics;
  return (
    <ContentUtilizationVideoCardContainer
      video={<HlsVideoPlayer videoSrc={videoSrc} width="428px" height="240px" />}
      metrics={
        <>
          <Text variant="h6">Metrics</Text>
          <Flex sx={{ marginTop: '8px' }} gap={16}>
            <MetricText label="Resolution" value={resolution} />
            <MetricText label="Length" value={length} />
          </Flex>
        </>
      }
    />
  );
};

const HlsVideoPlayer = ({
  videoSrc,
  width,
  height,
  videoStyle
}: {
  videoSrc: string;
  width: string;
  height: string;
  videoStyle?: React.CSSProperties;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoSrc || '');
      hls.attachMedia(videoRef.current || null);
    }
  }, [videoSrc]);

  return <video ref={videoRef} controls role="button" style={{ minWidth: '428px', width, height, ...videoStyle }} />;
};

export const ContentVideoContainer = ({ videoWidth, videoHeight }: { videoWidth: string; videoHeight: string }) => {
  return (
    <ContentUtilizationVideoCardContainer
      video={
        <ProductImage
          width={videoWidth}
          height={videoHeight}
          fallbackImageStyle={{ width: videoWidth, height: videoHeight, margin: '0' }}
          imageUri="TODO(tyan):changeme"
          mainDisplayTitle="TODO(tyan):changeme"
          secondaryDisplayTitle="TODO(tyan):changeme"
        />
      }
      metrics={
        <>
          <Text variant="h6">Metrics</Text>
          <Flex sx={{ marginTop: '8px' }} gap={16}>
            <MetricText label="Resolution" value="1920 x 1080px" />
            <MetricText label="Size" value="00:15 seconds" />
          </Flex>
        </>
      }
    />
  );
};

const MetricText = ({ label, value }: { label: string; value: string }) => {
  return (
    <Text variant="body1">
      <strong style={{ fontWeight: '500' }}>{label}:</strong> {value}
    </Text>
  );
};
